import React from 'react'
import { Chip, ChipProps } from '@mui/material'
import { formatStatusText } from '../../utils/textParser'

interface StatusChipColor {
  bgcolor: string
  color: string
}

const CANCELLED: StatusChipColor = { bgcolor: '#374151', color: '#FFFFFF' }
const COMPLETED: StatusChipColor = { bgcolor: '#16A34A', color: '#FFFFFF' }
const FAILED: StatusChipColor = { bgcolor: '#DC2626', color: '#FFFFFF' }
const WAITING: StatusChipColor = { bgcolor: '#F97316', color: '#FFFFFF' }
const RUNNING: StatusChipColor = { bgcolor: '#2563EB', color: '#FFFFFF' }
const DRAFT: StatusChipColor = { bgcolor: '#D1D5DB', color: '#1F2937' }
const SCHEDULED: StatusChipColor = { bgcolor: '#38BDF8', color: '#FFFFFF' }
const PAUSED: StatusChipColor = { bgcolor: '#eab308', color: '#151617' }

// Brand palette for campaign / execution status chips
export const CAMPAIGN_STATUS_CHIP_COLORS: Record<string, StatusChipColor> = {
  draft: DRAFT,
  scheduled: SCHEDULED,
  running: RUNNING,
  completed: COMPLETED,
  cancelled: CANCELLED,
  failed: FAILED,
  paused: PAUSED,
  partially_completed: WAITING, // displayed as "Pending Retry"
  queued_for_retry: WAITING,
  waiting_for_quota: WAITING,
  waiting_for_template_approval: WAITING,
}

// Brand palette when chip is shown on PageBanner (white pill background with status text color matching earlier status bgcolor)
export const CAMPAIGN_BANNER_STATUS_CHIP_COLORS: Record<string, string> = {
  // DRAFT.bgcolor and SCHEDULED.bgcolor has low contrast on white; darker shade used for readability
  draft: '#6b7280',
  scheduled: '#0284c7',
  running: RUNNING.bgcolor,
  completed: COMPLETED.bgcolor,
  cancelled: CANCELLED.bgcolor,
  failed: FAILED.bgcolor,
  paused: PAUSED.bgcolor,
  partially_completed: WAITING.bgcolor,
  queued_for_retry: WAITING.bgcolor,
  waiting_for_quota: WAITING.bgcolor,
  waiting_for_template_approval: WAITING.bgcolor,
}

export interface CampaignStatusChipProps extends Omit<ChipProps, 'label' | 'color' | 'sx' | 'variant'> {
  status?: string | null
  variant?: 'default' | 'banner'
  sx?: ChipProps['sx']
}

const CampaignStatusChip: React.FC<CampaignStatusChipProps> = ({
  status,
  size = 'small',
  variant = 'default',
  sx,
  ...rest
}) => {
  const resolvedStatus = status || 'draft'
  const { bgcolor, color } = CAMPAIGN_STATUS_CHIP_COLORS[resolvedStatus] || DRAFT

  if (variant === 'banner') {
    const textColor = CAMPAIGN_BANNER_STATUS_CHIP_COLORS[resolvedStatus] || bgcolor

    return (
      <Chip
        label={formatStatusText(resolvedStatus)}
        size={size}
        sx={[
          {
            height: 20,
            fontSize: '0.75rem',
            fontWeight: 700,
            bgcolor: '#fff',
            color: textColor,
            border: '1px solid',
            borderColor: 'rgba(255,255,255,0.8)',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}
      />
    )
  }

  return (
    <Chip
      label={formatStatusText(resolvedStatus)}
      size={size}
      sx={[
        { bgcolor, color },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    />
  )
}

export default CampaignStatusChip
